import type { HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { colors } from '../../../../styles/colors';
import { radii } from '../../../../styles/onboardingUiTokens';

export type ProgressBarProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
  /** 1-based current step. `0` renders an empty bar without progressbar semantics. */
  currentStep: number;
  /** Total number of steps. Must be >= 1. */
  totalSteps: number;
  /** Builds the accessible step description; defaults to "Schritt {n} von {total}". */
  valueText?: (currentStep: number, totalSteps: number) => string;
  /** Track height; defaults to `3px`. */
  thickness?: string;
  /** Track (empty) color; defaults to `colors.light`. */
  trackColor?: string;
  /** Fill color; defaults to `colors.primary`. */
  fillColor?: string;
  /**
   * When `true`, fixes the bar to the top of the viewport with safe-area aware insets.
   * Use for full-screen step flows or layered chrome. Defaults to `false` (inline / normal flow).
   */
  fixedToTop?: boolean;
  /** When `true`, drops the corner radius — for placements where the track is edge-to-edge with no
   * surrounding inset (e.g. the mobile header), where a rounded end reads as a rendering glitch. */
  flush?: boolean;
};

const defaultValueText = (current: number, total: number) => `Schritt ${current} von ${total}`;

export function ProgressBar({
  currentStep,
  totalSteps,
  valueText = defaultValueText,
  thickness = '3px',
  trackColor = colors.light,
  fillColor = colors.primary,
  fixedToTop = false,
  flush = false,
  'aria-label': ariaLabel,
  ...rest
}: ProgressBarProps) {
  const safeTotal = Math.max(1, totalSteps);
  const clampedStep = Math.max(0, Math.min(currentStep, safeTotal));
  const hasProgress = clampedStep > 0;
  const fillPercent = (clampedStep / safeTotal) * 100;

  return (
    <Track
      {...rest}
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuemin={1}
      aria-valuemax={safeTotal}
      aria-valuenow={hasProgress ? clampedStep : undefined}
      aria-valuetext={hasProgress ? valueText(clampedStep, safeTotal) : undefined}
      $thickness={thickness}
      $trackColor={trackColor}
      $fixedToTop={fixedToTop}
      $flush={flush}
    >
      <Fill $widthPercent={fillPercent} $fillColor={fillColor} $flush={flush} />
    </Track>
  );
}

const fixedToTopStyles = css`
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 10px);
  left: 20px;
  right: 20px;
  width: auto;
  z-index: 5;
  pointer-events: none;
`;

const Track = styled.div<{
  $thickness: string;
  $trackColor: string;
  $fixedToTop: boolean;
  $flush: boolean;
}>`
  position: relative;
  width: 100%;
  height: ${({ $thickness }) => $thickness};
  border-radius: ${({ $flush }) => ($flush ? '0' : radii.sm)};
  background: ${({ $trackColor }) => $trackColor};
  overflow: hidden;
  ${({ $fixedToTop }) => ($fixedToTop ? fixedToTopStyles : '')}
`;

const Fill = styled.div<{ $widthPercent: number; $fillColor: string; $flush: boolean }>`
  height: 100%;
  width: ${({ $widthPercent }) => $widthPercent}%;
  border-radius: ${({ $flush }) => ($flush ? '0' : radii.sm)};
  background: ${({ $fillColor }) => $fillColor};
  transition: width 0.3s ease;
`;
